import React, { useEffect } from 'react';
import TtScannerForm from './TtScannerForm';
import type { BatchItem, CccdScanResult, ScanMethod, TtCourse } from '../types';

const STORAGE_KEY_BATCH = 'tt_batch_items';
const STORAGE_KEY_BATCH_IDX = 'tt_batch_index';
const STORAGE_KEY_SINGLE = 'tt_single_result';

type Props = {
  open: boolean;
  items: BatchItem[];
  courses: TtCourse[];
  selectedCourseId: number | null;
  onClose: () => void;
  onSubmit: (data: CccdScanResult & { scanMethod: ScanMethod; pendingRecordId?: number }) => Promise<boolean>;
  /** When true, the inner scanner form renders with submit disabled
   *  + the teacher field hidden — used for status='accepted'/'entered'/
   *  'rejected' submissions where the teacher can only inspect, not
   *  modify. Backend rejects PUT in those statuses anyway; this just
   *  removes the misleading "Save" affordance from the UI. */
  readOnly?: boolean;
  /** Override label for the submit button (eg. "Cập nhật" when editing
   *  an existing pending submission instead of "Lưu hồ sơ"). */
  submitLabel?: string;
  submitIcon?: string;
  /** Hide the TeacherAutocomplete inside the scanner — used by the
   *  SupperTeacher portal where the teacher is implicit. */
  teacherFieldHidden?: boolean;
};

/**
 * Review & save modal — reuses TtScannerForm's full form layout in batch
 * review mode (thumbnail strip + image/fields split). Opens from the
 * "Chờ lưu hồ sơ" tab: either for a single row click or the "Xem & lưu
 * (N)" button that loads all done records at once.
 *
 * Keeps the main scanner tab uncluttered while still offering the rich
 * review UX when the operator is ready to commit records.
 */
const RecordDetailModal: React.FC<Props> = ({
  open, items, courses, selectedCourseId, onClose, onSubmit,
  readOnly = false, submitLabel, submitIcon,
  teacherFieldHidden = false,
}) => {
  // Clear stale localStorage when modal opens so TtScannerForm doesn't
  // merge the fresh modal items with leftovers from a previous session.
  useEffect(() => {
    if (open) {
      try {
        localStorage.removeItem(STORAGE_KEY_BATCH);
        localStorage.removeItem(STORAGE_KEY_BATCH_IDX);
        localStorage.removeItem(STORAGE_KEY_SINGLE);
      } catch { /* ignore */ }
    }
  }, [open]);

  // Close on Escape while modal is open. Use capture so the modal wins
  // the event before any inner handlers (e.g. ZoomOverlay) consume it.
  // When a ZoomOverlay is stacked on top, the modal only closes if zoom
  // isn't open — detected by peeking at .tt-scanner__avatar-zoom in DOM.
  //
  // After handling, stopImmediatePropagation + preventDefault so the
  // bubble-phase Escape listener in CourseDetailPage (which navigates
  // back to the course list) doesn't fire — without this, closing the
  // modal also yanks the user out of the course detail.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // If a zoom overlay is open, let IT handle Esc first.
      if (document.querySelector('.tt-scanner__avatar-zoom')) return;
      e.stopImmediatePropagation();
      e.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  if (!open || items.length === 0) return null;

  // Key changes when the set of items changes — forces TtScannerForm to
  // remount with a clean internal state. Otherwise the inner form would
  // APPEND the items to localStorage-persisted stale batchItems, and its
  // onBatchReviewClear callback (which we no-op here) would bring back
  // confusion. Using pendingRecordId.join() gives a stable fingerprint.
  const modalKey = items.map(i => i.pendingRecordId ?? i.fileName).join('|');

  const courseInfo = courses.find(c => c.id === selectedCourseId);

  return (
    <div
      className="tt-record-detail-modal__overlay"
      onClick={(e) => {
        // Close only when the click is DIRECTLY on the dim backdrop, not
        // bubbled from somewhere inside the modal. Inner event handlers
        // (dropdowns, buttons, file inputs…) occasionally forget to call
        // stopPropagation; with the stopPropagation-on-inner-div approach
        // alone, those clicks would escape and close the modal unexpectedly.
        // currentTarget vs target is the authoritative test for "backdrop
        // click" in React.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="tt-record-detail-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Proper header bar — replaces the previous floating × +
            floating course chip approach which overlapped the
            tt-scanner__header inside the form. Now the title sits in
            its own bar; the form below gets clean vertical space. */}
        <div className="tt-record-detail-modal__header">
          <div className="tt-record-detail-modal__header-info">
            <i className="material-icons">badge</i>
            <div className="tt-record-detail-modal__header-text">
              <div className="tt-record-detail-modal__header-title">
                {items.length > 1 ? `Xem & lưu ${items.length} hồ sơ` : 'Xem & lưu hồ sơ'}
              </div>
              {courseInfo && (
                <div className="tt-record-detail-modal__header-sub">
                  <i className="material-icons">school</i>
                  {courseInfo.name} — Hạng {courseInfo.hang}
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            className="tt-record-detail-modal__close"
            onClick={onClose}
            aria-label="Đóng (Esc)"
            title="Đóng (Esc)"
          >
            <i className="material-icons">close</i>
          </button>
        </div>
        <div className="tt-record-detail-modal__body">
          <TtScannerForm
            key={modalKey}
            onSubmit={readOnly
              // Read-only mount — no-op on submit so the button (if
              // accidentally enabled) does nothing. We also hide the
              // submit button itself via the readOnlyMode prop below.
              ? async () => false
              : onSubmit}
            batchReviewItems={items}
            isNangHangCourse={!!courseInfo?.isNangHang}
            teacherFieldHidden={teacherFieldHidden}
            onBatchReviewClear={() => {
              // IMPORTANT: do NOT call onClose here. TtScannerForm fires
              // this callback the moment it consumes batchReviewItems, so
              // closing the modal here would kill it right after opening.
              // Parent keeps reviewModalItems populated until user clicks
              // × or the last item is saved.
            }}
            readOnlyMode={readOnly}
            submitLabel={submitLabel || (readOnly ? 'Chỉ xem' : 'Lưu hồ sơ')}
            submitIcon={submitIcon || (readOnly ? 'visibility' : 'save')}
          />
        </div>
      </div>
    </div>
  );
};

export default RecordDetailModal;
